import ApisServiceApp from './ApisServiceApp'
import { apiUrl } from '@/config'
import { tokenStorage } from '@/services/tokenStorage'
import { apiRequest } from '@/utils/apiRequestUtils'
import { banHangListParamsFromQueryString } from '@/utils/banHangListQueryUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'

const PREFIX = '/hoa-don'

const blobResponseConfig = { responseType: 'blob' as const }

const parseBlobErrorIfJson = async (blob: Blob): Promise<never> => {
  const text = await blob.text()
  try {
    const j = JSON.parse(text) as { message?: string }
    throw new Error(j?.message || 'Yeu cau that bai')
  } catch (e) {
    if (e instanceof SyntaxError) throw new Error(text.trim().slice(0, 240) || 'Yeu cau that bai')
    throw e
  }
}

const ensureExcelBlob = async (blob: Blob): Promise<Blob> => {
  if (blob.type && blob.type.includes('application/json')) await parseBlobErrorIfJson(blob)
  return blob
}

const resolveApiBase = (): string => {
  const fromEnv = (apiUrl || '').replace(/\/$/, '')
  const fromAxios = String(ApisServiceApp.defaults.baseURL || '').replace(/\/$/, '')
  return fromEnv || fromAxios
}

export type HoaDonListSummary = {
  tongTienHang: number
  giamGia: number
  tongSauGiamGia: number
  khachCanTra: number
  khachDaTra: number
}

export type KhachHangCongNoResult = {
  rows: Record<string, unknown>[]
  total: number
  summary: HoaDonListSummary
}

export const fetchHoaDonList = (search: string): Promise<PagedListResult<Record<string, unknown>>> => {
  const params = banHangListParamsFromQueryString(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-hoa-don`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return { data: data as Record<string, unknown>[], paging, summary }
    }),
  )
}

export type HoaDonChiTietHangRow = {
  id: string
  hoaDonId: string
  maHoaDon: string
  thoiGian: string
  trangThai: string
  chiNhanhId: number | null
  tenChiNhanh: string
  nguoiBan: string
  khachHangId: string | null
  maKhachHang: string
  tenKhachHang: string
  hangHoaId: string
  maHangHoa: string
  tenHangHoa: string
  donViTinh: string | null
  soLuong: number
  /** Cân nặng dòng (kg) = cân 1 SP × soLuong. */
  tongCanNang: number
  /** Thể tích dòng (m³) = khối 1 SP × soLuong. */
  tongTheTich: number
  donGia: number
  giamGia: number
  giaBan: number
  thanhTien: number
  /** Giảm giá phiếu chia ngầm về dòng. */
  giamGiaChung: number
  /** Thu khác chia ngầm về dòng. */
  thuKhacChung: number
  /** thanhTien − giamGiaChung + thuKhacChung */
  tongCong: number
  ghiChu: string | null
}

export type HoaDonChiTietHangSummary = {
  /** Số dòng sản phẩm khớp filter. */
  soSanPham: number
  soLuong: number
  tongCanNang: number
  tongTheTich: number
  giamGia: number
  thanhTien: number
  giamGiaChung: number
  thuKhacChung: number
  tongCong: number
}

/** Danh sách chi tiết hàng hóa đơn — cùng bộ lọc danh sách hóa đơn. */
export const fetchHoaDonChiTietHangList = async (
  search: string,
): Promise<
  Omit<PagedListResult<HoaDonChiTietHangRow>, 'summary'> & { summary: HoaDonChiTietHangSummary | null }
> => {
  const params = banHangListParamsFromQueryString(search)
  const res = (await apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-chi-tiet-hang-hoa-don`, { params }),
  )) as Parameters<typeof normalizeListResponse>[0] & { summary?: HoaDonChiTietHangSummary }
  const { data, paging } = normalizeListResponse(res)
  return {
    data: data as HoaDonChiTietHangRow[],
    paging,
    summary: (res as { summary?: HoaDonChiTietHangSummary })?.summary ?? null,
  }
}

export type GiaBanGanNhat = {
  /** Id hàng hóa client gửi lên (có thể là mã con theo ĐVT). */
  hangHoaId: string
  /** Id hàng hóa thực tế trên dòng hóa đơn đã bán. */
  hangHoaIdDaBan: string
  soLuong: number
  donGia: number
  giamGia: number
  giamGiaPhanTram: number
  giaBan: number
  donViTinh: string | null
  hoaDonId: string
  maHoaDon: string
  thoiGian: string
}

/** Giá bán gần nhất của khách cho từng hàng hóa (chỉ hóa đơn hiệu lực). */
export const fetchGiaBanGanNhat = async (khachHangId: string, hangHoaIds: string[]): Promise<GiaBanGanNhat[]> => {
  if (!khachHangId || hangHoaIds.length === 0) return []
  const res = (await apiRequest(
    ApisServiceApp.get(`${PREFIX}/gia-ban-gan-nhat`, {
      params: { khachHangId, hangHoaIds: hangHoaIds.join(',') },
    }),
  )) as { data?: GiaBanGanNhat[] } | GiaBanGanNhat[]
  if (Array.isArray(res)) return res
  return Array.isArray(res?.data) ? res.data : []
}

/**
 * Lấy công nợ của 1 khách hàng: total aggregable + danh sách hóa đơn gần nhất.
 * - Tái dùng endpoint `/hoa-don/danh-sach-hoa-don` với filter `khachHangId`.
 * - `summary` từ BE là aggregate trên toàn bộ row khớp filter (không bị giới hạn paging).
 */
export const fetchKhachHangCongNo = async (
  khachHangId: string,
  opts?: { pageSize?: number },
): Promise<KhachHangCongNoResult> => {
  const pageSize = opts?.pageSize ?? 10
  const params = banHangListParamsFromQueryString(
    `?khachHangId=${encodeURIComponent(khachHangId)}&page=1&pageSize=${pageSize}`,
  )
  const res = (await apiRequest(ApisServiceApp.get(`${PREFIX}/danh-sach-hoa-don`, { params }))) as {
    data?: Record<string, unknown>[]
    results?: Record<string, unknown>[]
    summary?: Partial<HoaDonListSummary>
    paging?: { total?: number }
    total?: number
  }
  const rows = Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res?.results)
      ? res.results
      : []
  const total = res?.paging?.total ?? res?.total ?? rows.length
  const s = res?.summary ?? {}
  const summary: HoaDonListSummary = {
    tongTienHang: Number(s.tongTienHang ?? 0),
    giamGia: Number(s.giamGia ?? 0),
    tongSauGiamGia: Number(s.tongSauGiamGia ?? 0),
    khachCanTra: Number(s.khachCanTra ?? 0),
    khachDaTra: Number(s.khachDaTra ?? 0),
  }
  return { rows, total, summary }
}

const throwIfBlobAxiosError = async (err: unknown): Promise<never> => {
  const data = (err as { response?: { data?: unknown } })?.response?.data
  if (data instanceof Blob) await parseBlobErrorIfJson(data)
  throw err instanceof Error ? err : new Error(String((err as { message?: string })?.message || 'Yeu cau that bai'))
}

export const xuatHoaDonTongQuanBlob = async (search?: string): Promise<Blob> => {
  const params = search ? banHangListParamsFromQueryString(search) : {}
  try {
    const blob = (await ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-tong-quan-hoa-don`, {
      ...blobResponseConfig,
      params,
    })) as unknown as Blob
    return await ensureExcelBlob(blob)
  } catch (err) {
    return throwIfBlobAxiosError(err)
  }
}

export const xuatHoaDonChiTietBlob = async (search?: string): Promise<Blob> => {
  const params = search ? banHangListParamsFromQueryString(search) : {}
  try {
    const blob = (await ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-chi-tiet-hoa-don`, {
      ...blobResponseConfig,
      params,
    })) as unknown as Blob
    return await ensureExcelBlob(blob)
  } catch (err) {
    return throwIfBlobAxiosError(err)
  }
}

export const taiTemplateImportHoaDonBlob = async (): Promise<Blob> => {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/template-import-hoa-don`, blobResponseConfig),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const taoHoaDon = (body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/tao-hoa-don`, body))

/** FE parse Excel → POST JSON. */
export function nhapDanhSachHoaDon(rows: unknown[]) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/hoa-don-nhap-danh-sach`, rows))
}

/** @deprecated Dùng `nhapDanhSachHoaDon`. */
export const importHoaDonTuExcel = async (file: File): Promise<unknown> => {
  const base = resolveApiBase()
  if (!base) throw new Error('Chưa cấu hình API base URL')
  const formData = new FormData()
  formData.append('file', file)
  const token = tokenStorage.getAccessToken()
  const res = await fetch(`${base}${PREFIX}/import-hoa-don`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
  })
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = (await res.json()) as { message?: string }
      if (j?.message) msg = j.message
    } catch {
      // ignore
    }
    throw new Error(msg)
  }
  const text = await res.text()
  if (!text) return {}
  try {
    return JSON.parse(text) as unknown
  } catch {
    return {}
  }
}

export const fetchHoaDonDetail = (id: string | number) =>
  apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-hoa-don`).then((res: unknown) => ({
      data: normalizeDetailResponse(res as { data?: Record<string, unknown> }),
    })),
  )

export const capNhatHoaDon = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-hoa-don`, body))

/** Sửa tại chỗ ghi chú / kênh bán / người bán / giao hàng — không sinh phiên bản .01/.02. */
export const capNhatThongTinHoaDon = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/cap-nhat-thong-tin-hoa-don`, body))

export const xoaHoaDon = (id: string | number) =>
  apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-hoa-don`))

export const chuyenTrangThaiHoaDon = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-hoa-don`, body))

export const thanhToanHoaDon = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/thanh-toan-hoa-don`, body))

export const danhDauYeuThichHoaDon = (id: string | number, body?: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/danh-dau-yeu-thich-hoa-don`, body ?? {}))

export const saoChepHoaDon = (id: string | number) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/sao-chep-hoa-don`))
